import { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";

import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Button from "../../components/Button";
import FlashCoachLogo from "../../components/_Icons/FlashCoachLogo";
import TDCXLogo from "../../components/_Icons/TDCXLogo";
import getUserIP, { formatIPJSON } from "../../utils/getUserIP";
import styles from "../auth/Login.module.scss";

export default function ResetSecurityQuestion() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { addNotification } = useNotification();

  const token = new URLSearchParams(location.search).get("token");

  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [userIp, setUserIp] = useState(null);

  useEffect(() => {
    getUserIP().then(res => setUserIp(formatIPJSON(res)));
  }, []);

  const fetchQuestion = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiRequest.post(
        "/api/security-questions/reset/initiate",
        { token },
        { showLoader: false, skipNotification: true },
      );
      setQuestion(res.data.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        history.push("/");
        addNotification(t("login.tooManyAttempts"), "error");
      } else if (err?.response?.status === 401) {
        history.push("/");
        addNotification(t("login.sessionExpired"), "error");
      } else {
        addNotification(t("resetSecurityQuestion.failedLoad"), "error");
      }
    } finally {
      setLoading(false);
    }
  }, [token, addNotification, history, t]);

  useEffect(() => {
    if (!token) {
      addNotification(t("resetSecurityQuestion.missingToken"), "error");
      history.push("/");
      return;
    }
    fetchQuestion();
  }, [token, fetchQuestion, history, addNotification, t]);

  const handleSubmit = async () => {
    if (!answer) {
      addNotification(t("resetSecurityQuestion.pleaseAnswer"), "error");
      return;
    }
    try {
      setSubmitting(true);
      setAttemptsLeft(null);
      const res = await apiRequest.post("/api/security-questions/reset", {
        token,
        security_question_id: question.security_question_id,
        answer,
        userIp,
      }, { skipNotification: true });

      // SUCCESS - before redirecting to OTP screen, re-check both locks via initiateReset
      if (res.status === 200) {
        try {
          await apiRequest.post(
            "/api/security-questions/reset/initiate",
            { token },
            { showLoader: false, skipNotification: true },
          );
        } catch (lockErr) {
          if (lockErr?.response?.status === 403) {
            // OTP lock is still active - block redirect and show error here
            addNotification(t("login.tooManyAttempts"), "error");
            history.push("/");
            return;
          }
          // any other error from the lock check - non-blocking, let the redirect proceed
        }

        // not locked - safe to redirect to OTP screen
        addNotification(t("resetSecurityQuestion.verificationSuccess"), "success");
        history.push("/", {
          showOTPScreen: true,
          userID: res.data.user_id,
          isQRReset: true,
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      const attempts = err?.response?.data?.attempts_left;

      // INVALID ANSWER
      if (status === 400 && attempts !== undefined) {
        if (attempts <= 0) {
          history.push("/");
          addNotification(t("login.tooManyAttempts"), "error");
        } else {
          setAttemptsLeft(attempts);
        }
      }
      // LOCKED (403 from server when already locked on a fresh visit)
      else if (status === 403) {
        history.push("/");
        addNotification(t("login.tooManyAttempts"), "error");
      }
      // TOKEN EXPIRED OR INVALID
      else if (status === 401) {
        history.push("/");
        addNotification(t("login.sessionExpired"), "error");
      }
      // OTHER ERRORS
      else {
        addNotification(t("resetSecurityQuestion.verificationFailed"), "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const helpRequestButton = {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
    backgroundColor: "#E35646",
    padding: "5px",
    paddingTop: "15px",
    paddingBottom: "15px",
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "center",
    color: "white",
    cursor: "pointer",
  };

  const helpIcon = {
    display: "block",
    transform: "rotate(-90deg)",
    marginTop: "8px",
    lineHeight: "0.3",
    color: "white",
  };

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loginBlock}>
          <div className={styles.loginContent}>
            <div style={{ color: "#777", textAlign: "center" }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>

      {/* HELP BUTTON */}
      <div
        style={helpRequestButton}
        onClick={() => history.push("/help-request/welcome")}
      >
        <HelpOutlineIcon style={helpIcon} />
        <span className={styles.helpText}>Help Request Form</span>
      </div>

      <div className={styles.loginBlock}>

        {/* LOGO */}
        <div className={styles.logo}>
          <FlashCoachLogo />
        </div>

        <div style={{ height: 8 }} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className={styles.loginContent}>

            {/* HEADER */}
            <div className={styles.loginContent__formControl}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontWeight: 600, color: "#666", fontSize: 20, marginTop: "-30px" }}>
                  {t("resetSecurityQuestion.title")}
                </div>
                <div style={{ color: "#777", marginTop: 30, fontSize: 15 }}>
                  {t("resetSecurityQuestion.subtitle")}
                </div>
              </div>
            </div>

            {/* QUESTION */}
            <div className={styles.loginContent__formControl}>
              <div style={{ color: "#444", marginBottom: 6 }}>
                {question?.question_text}
              </div>

              <input
                value={answer}
                onChange={(e) => {
                  setAnswer(e.target.value);
                  setAttemptsLeft(null);
                }}
                placeholder={t("resetSecurityQuestion.placeholder")}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: `1px solid ${attemptsLeft !== null ? "#e53935" : "#ddd"}`,
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />

              {/* INLINE ERROR with bold attempts count */}
              {attemptsLeft !== null && (
                <div style={{ color: "#e53935", fontSize: 15, marginTop: 6 }}>
                  <Trans
                    i18nKey="resetSecurityQuestion.attemptsLeft"
                    values={{ count: attemptsLeft }}
                    components={{ strong: <strong /> }}
                  />
                </div>
              )}
            </div>

            {/* BUTTON */}
            <div className={styles.loginContent__formControl}>
              <Button
                color="primary"
                style={{ backgroundColor: "#f5b019" }}
                variant="contained"
                fullWidth
                isloading={submitting ? 1 : 0}
                type="submit"
              >
                {t("resetSecurityQuestion.continue")}
              </Button>
            </div>

            {/* BACK */}
            <div className={styles.loginContent__formControl}>
              <div style={{ textAlign: "center" }}>
                <Link to="/" className={styles.backLink}>
                  {t("resetSecurityQuestion.backToLogin")}
                </Link>
              </div>
            </div>

          </div>
        </form>
      </div>

      {/* FOOTER */}
      <div className={styles.footer}>
        <div className={styles.logo}>
          <TDCXLogo width={100} />
        </div>
        <div className={styles.copyright}>
          {t("common.copyright")}
        </div>
      </div>
    </div>
  );
}